import { FIRST_TOUCH_COOKIE, LAST_TOUCH_COOKIE } from '@/lib/tracking/config'

export type AttributionData = {
  source?: string
  medium?: string
  campaign?: string
  content?: string
  term?: string
  /** utm_id — Meta ad-level `{{campaign.id}}`; GA4's Manual campaign ID. */
  id?: string
  gclid?: string
  gbraid?: string
  wbraid?: string
  fbclid?: string
  referrer?: string
  landing_page?: string
  timestamp?: string
}

function readCookie(name: string): AttributionData | null {
  if (typeof document === 'undefined') return null
  const match = document.cookie.match(new RegExp(`(?:^|;\\s*)${name}=([^;]*)`))
  if (!match) return null
  try {
    return JSON.parse(decodeURIComponent(match[1])) as AttributionData
  } catch {
    return null
  }
}

function writeCookie(name: string, data: AttributionData, days: number): void {
  if (typeof document === 'undefined') return
  const expiryDate = new Date()
  expiryDate.setDate(expiryDate.getDate() + days)
  const value = encodeURIComponent(JSON.stringify(data))
  document.cookie = `${name}=${value}; expires=${expiryDate.toUTCString()}; path=/; SameSite=Lax; Secure`
}

export function getFirstTouch(): AttributionData | null {
  return readCookie(FIRST_TOUCH_COOKIE)
}

export function getLastTouch(): AttributionData | null {
  return readCookie(LAST_TOUCH_COOKIE)
}

export function setLastTouch(data: AttributionData): void {
  writeCookie(LAST_TOUCH_COOKIE, data, 90)
}

/**
 * Advance last-touch for a click-id-only arrival — a bare `?fbclid=` / `?gclid=`
 * visit, which EVERY Facebook and Instagram click carries even when the link
 * itself is untagged.
 *
 * A bare click id is an identifier, not a campaign signal. Replacing the record
 * with it would write a touch whose source/medium/campaign are all undefined,
 * and because JSON.stringify omits undefined keys the previous campaign would be
 * erased outright — a visitor acquired from a tagged YouTube link who later
 * returns via an untagged Facebook post would have their real source silently
 * destroyed, and the purchase would reach Stripe metadata with lt_source unset.
 *
 * So refresh the click id and the landing context, and keep the campaign fields
 * of the prior touch. When there is no prior touch, the click-id-only record is
 * the best available and is written as-is.
 */
export function mergeClickIdIntoLastTouch(data: AttributionData): void {
  const existing = getLastTouch()
  if (existing === null) {
    writeCookie(LAST_TOUCH_COOKIE, data, 90)
    return
  }
  writeCookie(
    LAST_TOUCH_COOKIE,
    {
      ...existing,
      gclid: data.gclid ?? existing.gclid,
      gbraid: data.gbraid ?? existing.gbraid,
      wbraid: data.wbraid ?? existing.wbraid,
      fbclid: data.fbclid ?? existing.fbclid,
      referrer: data.referrer ?? existing.referrer,
      landing_page: data.landing_page,
      timestamp: data.timestamp,
    },
    90
  )
}

export function setFirstTouchOnce(data: AttributionData): void {
  if (typeof document === 'undefined') return
  if (getFirstTouch() !== null) return
  writeCookie(FIRST_TOUCH_COOKIE, data, 365)
}
